import { useEffect, useState, useRef, ReactNode } from "react";
import { useQueryClient } from "@tanstack/react-query";
import type { Session, UserIdentity } from "@supabase/supabase-js";
import { UserProfile } from "@/types/dashboard";
import { authClient, SignUpData, SignInData } from "./authClient";
import { clearAccessToken, setAccessToken } from "./accessTokenStore";
import { supabase } from "../supabase";
import { AuthContext } from "./AuthContextType";
import { useAnalytics } from "@/hooks/useAnalytics";
import { reportError } from "@/integrations/sentry";
import { notifyRelayAuthChanged } from "@omnara/shared-js";
import { resetDashboardTransients } from "@omnara/shared-js/collections";
import { clearActivationFlag, trackActivationStep } from "@/lib/analytics/activationJourney";

const LAST_USED_PROVIDER_KEY = "omnara_last_login_provider";

const resolveProvider = (
  session: Session | null
): "google" | "apple" | "github" | "email" | null => {
  const socialProviders = ["google", "apple", "github"];

  const providerFromList =
    Array.isArray(session?.user?.app_metadata?.providers) &&
    session?.user?.app_metadata?.providers.find((p: string) => socialProviders.includes(p));

  const providerFromIdentity = session?.user?.identities?.find((identity: UserIdentity) =>
    socialProviders.includes(identity.provider)
  )?.provider;

  const rawProvider =
    providerFromList ||
    providerFromIdentity ||
    (session?.user?.app_metadata?.provider as string | undefined) ||
    null;

  if (socialProviders.includes(rawProvider)) {
    return rawProvider as "google" | "apple" | "github";
  }
  if (rawProvider === "email") {
    return "email";
  }
  return null;
};

const rememberProvider = (
  session: Session | null
): "google" | "apple" | "github" | "email" | null => {
  const normalized = resolveProvider(session);
  try {
    if (normalized) {
      localStorage.setItem(LAST_USED_PROVIDER_KEY, normalized);
    } else {
      localStorage.removeItem(LAST_USED_PROVIDER_KEY);
    }
  } catch {
    // Silently ignore storage errors
  }
  return normalized;
};

export function AuthProvider({ children }: { children: ReactNode }) {
  const queryClient = useQueryClient();
  const [user, setUser] = useState<UserProfile | null>(null);
  const [loading, setLoading] = useState(true);
  const { track, identify, reset } = useAnalytics();

  // Use refs to avoid re-running useEffect when analytics functions change identity
  const trackRef = useRef(track);
  const identifyRef = useRef(identify);
  const resetRef = useRef(reset);

  // Keep refs in sync
  useEffect(() => {
    trackRef.current = track;
    identifyRef.current = identify;
    resetRef.current = reset;
  }, [track, identify, reset]);

  const refreshSession = async () => {
    try {
      const sessionUser = await authClient.getSession();
      setUser(sessionUser);
    } catch (error) {
      console.warn("Failed to refresh auth session:", error);
      setUser(null);
    } finally {
      setLoading(false);
    }
  };

  const signUp = async (data: SignUpData) => {
    const response = await authClient.signUp(data);
    // Note: We intentionally do NOT set the user state here.
    // For email/password signup, the user hasn't confirmed their email yet.
    // Setting user would trigger the Landing page redirect before the
    // "Check your email" modal can display.
    // The user state will be set via onAuthStateChange when they confirm.

    // However, we DO identify them in analytics to track signup → confirmation conversion
    if (response.user) {
      identifyRef.current(response.user.id, {
        email: response.user.email,
        display_name: response.user.display_name,
        created_at: response.user.created_at,
      });
    }
  };

  const signIn = async (data: SignInData) => {
    const response = await authClient.signIn(data);
    setUser(response.user);
    // Note: auth_succeeded tracking happens in onAuthStateChange
    if (response.user) {
      identifyRef.current(response.user.id, {
        email: response.user.email,
        display_name: response.user.display_name,
        created_at: response.user.created_at,
      });
    }
  };

  const signInWithGoogle = async (redirectTo?: string) => {
    await authClient.signInWithGoogle(redirectTo);
    // Note: Google OAuth redirects, so we don't set user state here
  };

  const signInWithApple = async (redirectTo?: string) => {
    await authClient.signInWithApple(redirectTo);
    // Note: Apple OAuth redirects, so we don't set user state here
  };

  const signInWithGithub = async (redirectTo?: string) => {
    await authClient.signInWithGithub(redirectTo);
    // Note: GitHub OAuth redirects, so we don't set user state here
  };

  const signOut = async () => {
    try {
      await authClient.signOut();
      // State clearing and analytics tracking happens in onAuthStateChange
      // when SIGNED_OUT event fires
    } catch {
      // If signOut fails, manually clear state and reset analytics
      // (onAuthStateChange may not fire if network is down)
      setUser(null);
      resetRef.current();
    } finally {
      // Fallback: ensure analytics/user state are cleared even if SIGNED_OUT event is missed
      resetDashboardTransients();
      resetRef.current();
      setUser(null);
      clearActivationFlag();
    }
  };

  useEffect(() => {
    let mounted = true;

    // Helper to sync user with backend (handles errors gracefully)
    // This is called via setTimeout to avoid blocking the auth callback
    const syncWithBackend = async (sessionUser: Session["user"], accessToken?: string) => {
      if (!mounted) return;

      try {
        await authClient.syncUserWithBackend(sessionUser, { accessToken });
      } catch (error) {
        reportError(error, {
          context: "Failed to sync user with backend",
          tags: { feature: "web-auth", action: "backend-sync" },
        });
      }
    };

    // Single source of truth: onAuthStateChange handles ALL auth state
    // Important: Avoid async callbacks per Supabase docs to prevent deadlocks
    // See: https://supabase.com/docs/reference/javascript/auth-onauthstatechange
    const scheduleQueryRefresh = () => {
      setTimeout(() => {
        void queryClient.invalidateQueries({ refetchType: "active" });
      }, 0);
    };

    const {
      data: { subscription },
    } = supabase.auth.onAuthStateChange((event, session) => {
      if (!mounted) return;

      if (session?.user) {
        setAccessToken(session.access_token ?? null);
        const userProfile = {
          id: session.user.id,
          email: session.user.email || "",
          display_name: session.user.user_metadata?.display_name || null,
          created_at: session.user.created_at || new Date().toISOString(),
        };
        setUser(userProfile);

        // Identify user in analytics
        identifyRef.current(userProfile.id, {
          email: userProfile.email,
          display_name: userProfile.display_name,
          created_at: userProfile.created_at,
        });

        const normalizedProvider = rememberProvider(session);

        // Handle different auth events
        // Use setTimeout to defer async work and avoid blocking the callback
        if (event === "SIGNED_IN") {
          trackRef.current("auth_succeeded", {
            method: normalizedProvider || "email",
          });
          const accountAgeMs = Date.now() - new Date(session.user.created_at).getTime();
          if (accountAgeMs < 10 * 60 * 1000) {
            trackActivationStep(trackRef.current, "activation_start", "succeeded");
          }
          notifyRelayAuthChanged();
          // Sync with backend on sign-in (deferred to avoid callback deadlock)
          setTimeout(() => syncWithBackend(session.user, session.access_token), 0);
        } else if (event === "INITIAL_SESSION") {
          // Sync with backend on initial page load if user is authenticated
          notifyRelayAuthChanged();
          setTimeout(() => syncWithBackend(session.user, session.access_token), 0);
        } else if (event === "TOKEN_REFRESHED") {
          // Token was refreshed, resume relay connections if they were blocked
          notifyRelayAuthChanged();
          scheduleQueryRefresh();
        }
      } else {
        // No session - user is signed out
        clearAccessToken();
        if (event === "SIGNED_OUT") {
          trackRef.current("user_signed_out", {});
          resetRef.current();
        }
        setUser(null);
      }

      // Always set loading to false after processing any auth event
      setLoading(false);
    });

    // Trigger initial session check - this will fire INITIAL_SESSION event
    // which is handled by onAuthStateChange above
    supabase.auth.getSession().catch((error) => {
      reportError(error, {
        context: "Failed to get initial session",
        tags: { feature: "web-auth", action: "initial-session" },
      });
      if (mounted) {
        clearAccessToken();
        setUser(null);
        setLoading(false);
      }
    });

    return () => {
      mounted = false;
      subscription.unsubscribe();
    };
  }, [queryClient]);

  const value = {
    user,
    loading,
    signUp,
    signIn,
    signInWithGoogle,
    signInWithApple,
    signInWithGithub,
    signOut,
    refreshSession,
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}
